import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Plus, Trash2, Save, Eye, LogOut } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import AdminSidebar from '../components/AdminSidebar';
import LanguageToggle from '../components/LanguageToggle';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslations } from '../translations';
import { apiClient } from '../lib/apiClient';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlueZoneLayoutAdd = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = useTranslations(language);
  
  const [email, setEmail] = useState('');
  const [country, setCountry] = useState('');
  const [countries, setCountries] = useState([]);
  const [authorizedPersons, setAuthorizedPersons] = useState([
    { name: '', mobile: '', phone: '', email: '', title: '' }
  ]);
  const [showWizard, setShowWizard] = useState(false);
  const [gridSize] = useState({ width: 30, height: 25 });
  const [participantId, setParticipantId] = useState(null);
  const [baseWidth, setBaseWidth] = useState(''); // Boş başlasın
  const [baseHeight, setBaseHeight] = useState(''); // Boş başlasın
  const [selectedCells, setSelectedCells] = useState([]);
  const [openSides, setOpenSides] = useState(['north', 'east', 'south', 'west']);
  const [openSegments, setOpenSegments] = useState([]); // ['A1', 'A2', 'B1', etc.]
  const [showPreview, setShowPreview] = useState(false);
  const [showSegmentControls, setShowSegmentControls] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const [dragCurrent, setDragCurrent] = useState(null);
  const [dragMode, setDragMode] = useState('add');
  const [activeSegmentTab, setActiveSegmentTab] = useState('A');

  useEffect(() => {
    fetchCountries();
  }, []);

  useEffect(() => {
    // Width ve height girilince otomatik base alanı seç
    if (baseWidth && baseHeight) {
      const w = parseInt(baseWidth);
      const h = parseInt(baseHeight);
      if (w > 0 && h > 0) {
        const cells = [];
        for (let x = 0; x < w; x++) {
          for (let y = 0; y < h; y++) {
            cells.push([x, y]);
          }
        }
        setSelectedCells(cells);
      }
    } else {
      setSelectedCells([]);
    }
  }, [baseWidth, baseHeight]);

  const fetchCountries = async () => {
    try {
      const token = localStorage.getItem('cop31_ultra_token');
      const response = await apiClient.get(`/ultra-admin/countries`);
      setCountries(response.data.countries);
    } catch (error) {
      console.error('Failed to fetch countries');
    }
  };

  const addPerson = () => {
    setAuthorizedPersons([...authorizedPersons, { name: '', mobile: '', phone: '', email: '', title: '' }]);
  };

  const removePerson = (index) => {
    setAuthorizedPersons(authorizedPersons.filter((_, i) => i !== index));
  };

  const updatePerson = (index, field, value) => {
    const updated = [...authorizedPersons];
    updated[index][field] = value;
    setAuthorizedPersons(updated);
  };

  const proceedToWizard = async () => {
    if (!email || !country) {
      alert(language === 'tr' ? 'Email ve ulke gerekli' : 'Email and country required');
      return;
    }
    
    const hasValidPerson = authorizedPersons.some(p => p.name && p.name.trim() !== '');
    if (!hasValidPerson) {
      alert(language === 'tr' ? 'En az bir yetkili kisi adi giriniz' : 'Enter at least one person name');
      return;
    }
    
    const token = localStorage.getItem('cop31_ultra_token');
    const userDataStr = localStorage.getItem('cop31_user');
    
    console.log('=== CREATE PARTICIPANT ===');
    console.log('Token exists:', !!token);
    
    if (userDataStr) {
      const userData = JSON.parse(userDataStr);
      console.log('User role:', userData.role);
      console.log('User ID:', userData.user_id);
    }
    
    const validPersons = authorizedPersons.filter(p => p.name && p.name.trim() !== '');
    
    try {
      console.log('Sending POST request...');
      const response = await apiClient.post(`/ultra-admin/blue-zone-layouts`, {
        email,
        country,
        authorized_persons: validPersons,
        layout: {
          base_width: 10,
          base_height: 8,
          grid_cells: [],
          indentations: [],
          open_sides: [],
          closed_sides: ['north', 'south', 'east', 'west'],
          total_area: 80
        }
      });
      
      console.log('✅ Response:', response.data);
      setParticipantId(response.data.id);
      setShowWizard(true);
    } catch (error) {
      console.error('❌ Create failed:', error.response?.status, error.response?.data);
      const errorMsg = error.response?.data?.detail || (language === 'tr' ? 'Kayit basarisiz' : 'Save failed');
      alert(errorMsg);
    }
  };

  const toggleCell = (x, y) => {
    const w = parseInt(baseWidth) || 0;
    const h = parseInt(baseHeight) || 0;
    const insideBase = x < w && y < h;
    
    const cellIndex = selectedCells.findIndex(cell => cell[0] === x && cell[1] === y);
    
    if (cellIndex >= 0) {
      // Hücre seçili - kaldır
      if (insideBase) {
        // Base içinden çıkarıyoruz (girinti oluşturacak)
        // Eğer base dışında extrusion varsa, uyar
        const hasExtrusion = selectedCells.some(([cx, cy]) => cx >= w || cy >= h);
        if (hasExtrusion) {
          alert(language === 'tr' ? 'Once cikinti alanlarini kaldirin!' : 'Remove extrusions first!');
          return;
        }
      }
      setSelectedCells(selectedCells.filter((_, idx) => idx !== cellIndex));
    } else {
      // Hücre seçili değil - ekle
      if (!insideBase) {
        // Base dışına ekliyoruz (extrusion)
        // Base içindeki seçili hücre sayısını kontrol et
        const baseSelectedCount = selectedCells.filter(([cx, cy]) => cx < w && cy < h).length;
        const hasIndentation = baseSelectedCount < (w * h);
        
        if (hasIndentation) {
          alert(language === 'tr' ? 'Girinti varken cikinti eklenemez! (Base alanini tamamlayin)' : 'Cannot add extrusion with indentations! (Complete base area first)');
          return;
        }
      }
      setSelectedCells([...selectedCells, [x, y]]);
    }
  };

  const isCellSelected = (x, y) => {
    return selectedCells.some(cell => cell[0] === x && cell[1] === y);
  };

  const isInsideBase = (x, y) => {
    const w = parseInt(baseWidth) || 0;
    const h = parseInt(baseHeight) || 0;
    return x < w && y < h;
  };

  const toggleSide = (side) => {
    if (openSides.includes(side)) {
      setOpenSides(openSides.filter(s => s !== side));
    } else {
      setOpenSides([...openSides, side]);
    }
  };

  // ── Drag-to-draw ──────────────────────────────────────────────────────────
  const getDragRect = () => {
    if (!dragStart || !dragCurrent) return [];
    const minX = Math.min(dragStart.x, dragCurrent.x);
    const maxX = Math.max(dragStart.x, dragCurrent.x);
    const minY = Math.min(dragStart.y, dragCurrent.y);
    const maxY = Math.max(dragStart.y, dragCurrent.y);
    const cells = [];
    for (let x = minX; x <= maxX; x++)
      for (let y = minY; y <= maxY; y++)
        cells.push([x, y]);
    return cells;
  };

  const handleCellMouseDown = (e, x, y) => {
    e.preventDefault();
    const isSelected = selectedCells.some(([cx, cy]) => cx === x && cy === y);
    setDragMode(isSelected ? 'remove' : 'add');
    setDragStart({ x, y });
    setDragCurrent({ x, y });
    setIsDragging(true);
  };

  const handleCellMouseEnter = (x, y) => {
    if (isDragging) setDragCurrent({ x, y });
  };

  const applyDrag = () => {
    if (!isDragging) return;
    const dragCells = getDragRect();
    if (dragCells.length > 0) {
      if (dragMode === 'add') {
        const newCells = [...selectedCells];
        dragCells.forEach(([x, y]) => {
          if (!newCells.some(([cx, cy]) => cx === x && cy === y))
            newCells.push([x, y]);
        });
        setSelectedCells(newCells);
      } else {
        setSelectedCells(selectedCells.filter(([cx, cy]) =>
          !dragCells.some(([dx, dy]) => dx === cx && dy === cy)
        ));
      }
    }
    setIsDragging(false);
    setDragStart(null);
    setDragCurrent(null);
  };
  // ─────────────────────────────────────────────────────────────────────────

  // Doğru m² hesaplama
  const totalArea = useMemo(() => {
    return selectedCells.filter(([x, y]) => x < (parseInt(baseWidth)||0) && y < (parseInt(baseHeight)||0)).length;
  }, [selectedCells]);

  const handleLogout = () => {
    localStorage.clear();
    navigate('/giris');
  };

  // Simple → Advanced geçişinde openSides'dan openSegments'ı her zaman senkronize et
  const handleToggleAdvanced = () => {
    const goingAdvanced = !showSegmentControls;
    setShowSegmentControls(goingAdvanced);
    if (goingAdvanced) {
      const sideMap = { north: 'A', east: 'B', south: 'C', west: 'D' };
      const allGroups = getAllSegmentGroups();
      const syncedOpen = allGroups
        .filter(g => {
          const letter = g.id[0];
          const side = Object.entries(sideMap).find(([, l]) => l === letter)?.[0];
          return side && openSides.includes(side);
        })
        .map(g => g.id);
      setOpenSegments(syncedOpen);
    }
  };

  // Segment'leri grupla - bitişik olanları birleştir ve A1, A2 gibi isimlendir
  const groupSegments = (segments, sideLetter) => {
    if (segments.length === 0) return [];
    
    const groups = [];
    let currentGroup = [segments[0]];
    
    for (let i = 1; i < segments.length; i++) {
      const prev = segments[i - 1];
      const curr = segments[i];
      
      // N/S (horizontal, y1===y2): same Y + X-adjacent
      // E/W (vertical, x1===x2): same X + Y-adjacent
      const isHorizontal = prev.y1 === prev.y2;
      const isAdjacent = isHorizontal
        ? (curr.x1 === prev.x2 && curr.y1 === prev.y1)
        : (curr.y1 === prev.y2 && curr.x1 === prev.x1);
      
      if (isAdjacent) {
        currentGroup.push(curr);
      } else {
        groups.push({ id: `${sideLetter}${groups.length + 1}`, segments: currentGroup });
        currentGroup = [curr];
      }
    }
    
    groups.push({ id: `${sideLetter}${groups.length + 1}`, segments: currentGroup });
    return groups;
  };

  // Tüm kenarlar için grupları hesapla
  const getAllSegmentGroups = () => {
    return [
      ...groupSegments(getEdgeSegments('north'), 'A'),
      ...groupSegments(getEdgeSegments('east'), 'B'),
      ...groupSegments(getEdgeSegments('south'), 'C'),
      ...groupSegments(getEdgeSegments('west'), 'D')
    ];
  };

  const toggleSegment = (segmentId) => {
    if (openSegments.includes(segmentId)) {
      setOpenSegments(openSegments.filter(s => s !== segmentId));
    } else {
      setOpenSegments([...openSegments, segmentId]);
    }
  };

  // Basit segment rengi - sadece openSides kullanarak
  const getSegmentColor = (side, x, y) => {
    const w = parseInt(baseWidth) || 0;
    const h = parseInt(baseHeight) || 0;
    
    let checkX = x, checkY = y;
    if (side === 'north') checkY = y;
    else if (side === 'south') checkY = y - 1;
    else if (side === 'east') checkX = x - 1;
    else if (side === 'west') checkX = x;
    
    const isSelected = selectedCells.some(([cx, cy]) => cx === checkX && cy === checkY);
    const isExtrusion = isSelected && (checkX >= w || checkY >= h);
    
    if (isExtrusion) return '#22c55e';
    
    return openSides.includes(side) ? '#22c55e' : '#ef4444';
  };

  // Segment color - openSegments kullanarak
  const getSegmentColorAdvanced = (side, x, y, segmentGroups) => {
    const w = parseInt(baseWidth) || 0;
    const h = parseInt(baseHeight) || 0;
    
    let checkX = x, checkY = y;
    if (side === 'north') checkY = y;
    else if (side === 'south') checkY = y - 1;
    else if (side === 'east') checkX = x - 1;
    else if (side === 'west') checkX = x;
    
    const isSelected = selectedCells.some(([cx, cy]) => cx === checkX && cy === checkY);
    const isExtrusion = isSelected && (checkX >= w || checkY >= h);
    
    if (isExtrusion) return '#22c55e';
    
    // Hangi gruba ait?
    const sideLetter = side === 'north' ? 'A' : side === 'east' ? 'B' : side === 'south' ? 'C' : 'D';
    const sideGroups = segmentGroups.filter(g => g.id.startsWith(sideLetter));
    
    for (const group of sideGroups) {
      const inGroup = group.segments.some(seg => 
        (seg.x1 === x && seg.y1 === y) || (seg.x2 === x && seg.y2 === y)
      );
      if (inGroup) {
        return openSegments.includes(group.id) ? '#22c55e' : '#ef4444';
      }
    }
    
    return openSides.includes(side) ? '#22c55e' : '#ef4444';
  };

  const getEdgeSegments = (side) => {
    if (!selectedCells.length) return [];
    
    const w = parseInt(baseWidth) || 0;
    const h = parseInt(baseHeight) || 0;
    const segments = [];
    
    if (side === 'north') {
      // Her x koordinatı için, o sütundaki en üstteki seçili hücreyi bul
      for (let x = 0; x < Math.max(w, 30); x++) {
        const colCells = selectedCells.filter(([cx]) => cx === x);
        if (colCells.length > 0) {
          const minY = Math.min(...colCells.map(([, cy]) => cy));
          segments.push({ x1: x, y1: minY, x2: x + 1, y2: minY });
        }
      }
    } else if (side === 'south') {
      for (let x = 0; x < Math.max(w, 30); x++) {
        const colCells = selectedCells.filter(([cx]) => cx === x);
        if (colCells.length > 0) {
          const maxY = Math.max(...colCells.map(([, cy]) => cy));
          segments.push({ x1: x, y1: maxY + 1, x2: x + 1, y2: maxY + 1 });
        }
      }
    } else if (side === 'east') {
      for (let y = 0; y < Math.max(h, 25); y++) {
        const rowCells = selectedCells.filter(([, cy]) => cy === y);
        if (rowCells.length > 0) {
          const maxX = Math.max(...rowCells.map(([cx]) => cx));
          segments.push({ x1: maxX + 1, y1: y, x2: maxX + 1, y2: y + 1 });
        }
      }
    } else if (side === 'west') {
      for (let y = 0; y < Math.max(h, 25); y++) {
        const rowCells = selectedCells.filter(([, cy]) => cy === y);
        if (rowCells.length > 0) {
          const minX = Math.min(...rowCells.map(([cx]) => cx));
          segments.push({ x1: minX, y1: y, x2: minX, y2: y + 1 });
        }
      }
    }
    
    return segments;
  };

  const saveLayout = async () => {
    console.log('=== SAVE LAYOUT CALLED ===');
    console.log('participantId:', participantId);
    console.log('baseWidth:', baseWidth, 'baseHeight:', baseHeight);
    console.log('selectedCells count:', selectedCells.length);
    console.log('totalArea:', totalArea);
    
    if (!participantId) {
      alert(language === 'tr'
        ? 'HATA: Participant ID bulunamadı! Lütfen sayfayı yenileyip tekrar deneyin.'
        : 'ERROR: Participant ID not found! Please refresh the page and try again.');
      console.error('participantId is NULL or UNDEFINED!');
      return;
    }
    
    if (!baseWidth || !baseHeight) {
      alert(language === 'tr' ? 'En ve boy giriniz!' : 'Enter width and height!');
      return;
    }
    
    const closedSides = ['north', 'south', 'east', 'west'].filter(s => !openSides.includes(s));
    const token = localStorage.getItem('cop31_ultra_token');
    
    const layoutData = {
      base_width: parseInt(baseWidth),
      base_height: parseInt(baseHeight),
      grid_cells: selectedCells,
      indentations: [],
      open_sides: openSides,
      closed_sides: closedSides,
      open_segments: openSegments,
      total_area: totalArea
    };
    
    console.log('Sending layout data:', layoutData);
    
    try {
      const response = await apiClient.put(
        `/ultra-admin/blue-zone-layouts/${participantId}`, 
        { 
          layout: layoutData 
        }
      );
      
      console.log('✅ Layout saved successfully!', response.data);
      alert(language === 'tr' ? 'Layout basariyla kaydedildi!' : 'Layout saved successfully!');
      navigate('/ultra-admin/blue-zone');
    } catch (error) {
      console.error('❌ Save failed:', error.response?.data);
      alert(language === 'tr' ? 'Kayit HATASI: ' + (error.response?.data?.detail || error.message) : 'Save ERROR: ' + (error.response?.data?.detail || error.message));
    }
  };

  if (!showWizard) {
    return (
      <div className="min-h-screen bg-gray-50 flex">
        <AdminSidebar />
        <div className="flex-1">
          <nav className="bg-white border-b sticky top-0 z-40">
            <div className="px-8 flex justify-between items-center h-16">
              <h1 className="text-xl font-bold">{t('add_participant')}</h1>
              <div className="flex gap-4">
                <LanguageToggle variant="admin" />
                <Button onClick={handleLogout} variant="outline" className="rounded-full">
                  <LogOut className="h-4 w-4 mr-2" />{t('logout')}
                </Button>
              </div>
            </div>
          </nav>
          
          <div className="p-8 max-w-4xl">
            <Card className="mb-6 rounded-3xl shadow-lg">
              <CardHeader>
                <CardTitle>{t('participant_email')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>{t('email')}</Label>
                  <Input id="pemail" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-xl" />
                </div>
                <div>
                  <Label>{t('country')}</Label>
                  <select value={country} onChange={(e) => setCountry(e.target.value)} className="w-full px-4 py-2 border rounded-xl">
                    <option value="">{t('select_country')}</option>
                    {countries.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-1 gap-4">
                  <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-3">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>
                    <div className="text-xs text-amber-900 leading-relaxed">
                      <p className="font-semibold">
                        {language === 'tr' ? 'Stand / Hall atama burada yapılmaz.' : 'Stand / Hall assignment is handled elsewhere.'}
                      </p>
                      <p className="mt-0.5 text-amber-800">
                        {language === 'tr'
                          ? 'Bu formda yalnızca katılımcı bilgileri ve layout oluşturulur. Gerçek hall & stand ataması için Hall Management → Hall seçin → Stand oluştur / ata akışını kullanın. Böylece "hayalet" atamalar üretilemez.'
                          : 'This form only captures participant info and layout. Real hall & stand assignment happens via Hall Management → select Hall → create / assign Stand. This prevents "phantom" assignments.'}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="mb-6 rounded-3xl shadow-lg">
              <CardHeader>
                <div className="flex justify-between">
                  <CardTitle>{t('authorized_persons')}</CardTitle>
                  <Button onClick={addPerson} className="bg-purple-700 rounded-full">
                    <Plus className="h-4 w-4 mr-2" />{t('add_person')}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {authorizedPersons.map((person, idx) => (
                  <div key={idx} className="bg-gray-50 rounded-2xl p-4 relative">
                    <div className="grid grid-cols-2 gap-4">
                      <div><Label>{t('full_name')}</Label><Input value={person.name} onChange={(e) => updatePerson(idx, 'name', e.target.value)} className="rounded-xl" /></div>
                      <div><Label>{t('mobile_phone')}</Label><Input value={person.mobile} onChange={(e) => updatePerson(idx, 'mobile', e.target.value)} className="rounded-xl" /></div>
                      <div><Label>{t('phone')}</Label><Input value={person.phone} onChange={(e) => updatePerson(idx, 'phone', e.target.value)} className="rounded-xl" /></div>
                      <div><Label>{t('email')}</Label><Input type="email" value={person.email} onChange={(e) => updatePerson(idx, 'email', e.target.value)} className="rounded-xl" /></div>
                      <div className="col-span-2"><Label>{t('title')}</Label><Input value={person.title} onChange={(e) => updatePerson(idx, 'title', e.target.value)} className="rounded-xl" /></div>
                    </div>
                    {authorizedPersons.length > 1 && (
                      <Button onClick={() => removePerson(idx)} variant="ghost" size="sm" className="absolute top-2 right-2 text-red-600">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>

            <Button onClick={proceedToWizard} className="w-full bg-purple-700 rounded-full py-6 text-lg">
              {t('create_layout')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <AdminSidebar />
      <div className="flex-1 p-8">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl font-bold mb-8">{t('layout_wizard')}</h1>
          
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <Card className="rounded-3xl shadow-lg">
                <CardHeader>
                  <CardTitle>{language === 'tr' ? '1m x 1m Grid - T\u0131klayarak Se\u00e7in' : '1m x 1m Grid - Click to Select'}</CardTitle>
                  <p className="text-sm text-gray-600">{language === 'tr' ? 'Mavi kareler = Se\u00e7ili alan. Kesikli \u00e7izgi = Ana alan s\u0131n\u0131r\u0131' : 'Blue = Selected. Dashed = Base boundary'}</p>
                </CardHeader>
                <CardContent>
                  <div className="bg-gray-50 rounded-xl p-4">
                    {!baseWidth || !baseHeight ? (
                      <div className="min-h-96 flex items-center justify-center text-gray-500">
                        <p>{language === 'tr' ? 'L\u00fctfen en ve boy de\u011ferlerini giriniz' : 'Please enter width and height'}</p>
                      </div>
                    ) : (() => {
                      const w = parseInt(baseWidth);
                      const h = parseInt(baseHeight);
                      const cellSize = 20;
                      const totalGridW = w * cellSize;
                      const totalGridH = h * cellSize;
                      const svgTotalW = totalGridW + 100;
                      const svgTotalH = totalGridH + 100;
                      return (
                        <svg
                          viewBox={`0 0 ${svgTotalW} ${svgTotalH}`}
                          width="100%"
                          style={{maxHeight: '500px', display: 'block', userSelect: 'none', cursor: isDragging ? 'crosshair' : 'default'}}
                          preserveAspectRatio="xMidYMid meet"
                          onMouseUp={applyDrag}
                          onMouseLeave={applyDrag}
                        >
                          {/* Grid lines - sadece alan içinde */}
                          {Array.from({ length: w + 1 }).map((_, i) => (
                            <line key={`v${i}`} x1={50 + i * cellSize} y1={50} x2={50 + i * cellSize} y2={50 + h * cellSize} stroke="#cbd5e1" strokeWidth="1" />
                          ))}
                          {Array.from({ length: h + 1 }).map((_, i) => (
                            <line key={`h${i}`} x1={50} y1={50 + i * cellSize} x2={50 + w * cellSize} y2={50 + i * cellSize} stroke="#cbd5e1" strokeWidth="1" />
                          ))}
                          
                          {/* Seçili kareler */}
                          {selectedCells.map(([x, y]) => {
                            const inside = x < w && y < h;
                            return (
                              <rect
                                key={`sel-${x}-${y}`}
                                x={50 + x * cellSize}
                                y={50 + y * cellSize}
                                width={cellSize}
                                height={cellSize}
                                fill={inside ? '#3b82f6' : '#93c5fd'}
                              />
                            );
                          })}
                          
                          {/* Draggable overlay - tüm grid */}
                          {Array.from({ length: w + 10 }).map((_, x) => 
                            Array.from({ length: h + 10 }).map((_, y) => (
                              <rect
                                key={`c-${x}-${y}`}
                                x={50 + x * cellSize}
                                y={50 + y * cellSize}
                                width={cellSize}
                                height={cellSize}
                                fill="transparent"
                                stroke="#e2e8f0"
                                strokeWidth="0.5"
                                style={{cursor: 'crosshair'}}
                                onMouseDown={(e) => handleCellMouseDown(e, x, y)}
                                onMouseEnter={() => handleCellMouseEnter(x, y)}
                              />
                            ))
                          )}
                          
                          {/* Drag preview */}
                          {isDragging && getDragRect().map(([px, py]) => (
                            <rect
                              key={`dp-${px}-${py}`}
                              x={50 + px * cellSize} y={50 + py * cellSize}
                              width={cellSize} height={cellSize}
                              fill={dragMode === 'add' ? 'rgba(59,130,246,0.45)' : 'rgba(239,68,68,0.45)'}
                              stroke={dragMode === 'add' ? '#3b82f6' : '#ef4444'}
                              strokeWidth="1"
                              style={{pointerEvents: 'none'}}
                            />
                          ))}
                          
                          {/* Base sınırı - TAM mavi alanın kenarında */}
                          <rect 
                            x={50} 
                            y={50} 
                            width={w * cellSize} 
                            height={h * cellSize} 
                            fill="none" 
                            stroke="#0ea5e9" 
                            strokeWidth="3" 
                            strokeDasharray="8,4" 
                          />
                          
                          {/* Kenar çizgileri - Gelişmiş mod varsa grup bazlı */}
                          {selectedCells.length > 0 && (() => {
                            const segmentGroups = showSegmentControls ? getAllSegmentGroups() : [];
                            
                            return (
                              <>
                                {getEdgeSegments('north').map((seg, i) => (
                                  <line 
                                    key={`n-${i}`}
                                    x1={50 + seg.x1 * cellSize} 
                                    y1={50 + seg.y1 * cellSize} 
                                    x2={50 + seg.x2 * cellSize} 
                                    y2={50 + seg.y2 * cellSize}
                                    stroke={showSegmentControls ? getSegmentColorAdvanced('north', seg.x1, seg.y1, segmentGroups) : getSegmentColor('north', seg.x1, seg.y1)}
                                    strokeWidth="8"
                                    strokeLinecap="square"
                                  />
                                ))}
                                
                                {getEdgeSegments('south').map((seg, i) => (
                                  <line 
                                    key={`s-${i}`}
                                    x1={50 + seg.x1 * cellSize} 
                                    y1={50 + seg.y1 * cellSize} 
                                    x2={50 + seg.x2 * cellSize} 
                                    y2={50 + seg.y2 * cellSize}
                                    stroke={showSegmentControls ? getSegmentColorAdvanced('south', seg.x1, seg.y1, segmentGroups) : getSegmentColor('south', seg.x1, seg.y1)}
                                    strokeWidth="8"
                                    strokeLinecap="square"
                                  />
                                ))}
                                
                                {getEdgeSegments('east').map((seg, i) => (
                                  <line 
                                    key={`e-${i}`}
                                    x1={50 + seg.x1 * cellSize} 
                                    y1={50 + seg.y1 * cellSize} 
                                    x2={50 + seg.x2 * cellSize} 
                                    y2={50 + seg.y2 * cellSize}
                                    stroke={showSegmentControls ? getSegmentColorAdvanced('east', seg.x1, seg.y1, segmentGroups) : getSegmentColor('east', seg.x1, seg.y1)}
                                    strokeWidth="8"
                                    strokeLinecap="square"
                                  />
                                ))}
                                
                                {getEdgeSegments('west').map((seg, i) => (
                                  <line 
                                    key={`w-${i}`}
                                    x1={50 + seg.x1 * cellSize} 
                                    y1={50 + seg.y1 * cellSize} 
                                    x2={50 + seg.x2 * cellSize} 
                                    y2={50 + seg.y2 * cellSize}
                                    stroke={showSegmentControls ? getSegmentColorAdvanced('west', seg.x1, seg.y1, segmentGroups) : getSegmentColor('west', seg.x1, seg.y1)}
                                    strokeWidth="8"
                                    strokeLinecap="square"
                                  />
                                ))}
                              </>
                            );
                          })()}
                          
                          {/* Segment etiketleri - Gelişmiş modda A1, A2 göster */}
                          {showSegmentControls && getAllSegmentGroups().map(group => {
                            const segs = group.segments;
                            if (!segs || segs.length === 0) return null;
                            const side = group.id[0];
                            const minX = Math.min(...segs.map(s => s.x1));
                            const maxX = Math.max(...segs.map(s => s.x2));
                            const minY = Math.min(...segs.map(s => s.y1));
                            const maxY = Math.max(...segs.map(s => s.y2));
                            let cx, cy;
                            if (side === 'A') {
                              cx = 50 + (minX + maxX) / 2 * cellSize;
                              cy = 50 + segs[0].y1 * cellSize - 13;
                            } else if (side === 'C') {
                              cx = 50 + (minX + maxX) / 2 * cellSize;
                              cy = 50 + segs[0].y1 * cellSize + 13;
                            } else if (side === 'B') {
                              cx = 50 + segs[0].x1 * cellSize + 13;
                              cy = 50 + (minY + maxY) / 2 * cellSize;
                            } else {
                              cx = 50 + segs[0].x1 * cellSize - 13;
                              cy = 50 + (minY + maxY) / 2 * cellSize;
                            }
                            const isOpen = openSegments.includes(group.id);
                            const sideColors = { A: '#ef4444', B: '#f59e0b', C: '#8b5cf6', D: '#10b981' };
                            return (
                              <g key={`lbl-${group.id}`} style={{pointerEvents: 'none'}}>
                                <circle cx={cx} cy={cy} r={12} fill={isOpen ? '#16a34a' : sideColors[side]} opacity="0.92" />
                                <text x={cx} y={cy + 4} textAnchor="middle" fill="white" fontSize={group.id.length > 2 ? "8" : "9"} fontWeight="bold" fontFamily="monospace">{group.id}</text>
                              </g>
                            );
                          })}
                          
                          {/* A,B,C,D etiketleri */}
                          <g><circle cx={50 + w * cellSize / 2} cy={25} r="20" fill="#ef4444" /><text x={50 + w * cellSize / 2} y={32} textAnchor="middle" fontSize="18" fontWeight="bold" fill="white">A</text></g>
                          <g><circle cx={50 + w * cellSize + 30} cy={50 + h * cellSize / 2} r="20" fill="#f59e0b" /><text x={50 + w * cellSize + 30} y={50 + h * cellSize / 2 + 7} textAnchor="middle" fontSize="18" fontWeight="bold" fill="white">B</text></g>
                          <g><circle cx={50 + w * cellSize / 2} cy={50 + h * cellSize + 30} r="20" fill="#8b5cf6" /><text x={50 + w * cellSize / 2} y={50 + h * cellSize + 37} textAnchor="middle" fontSize="18" fontWeight="bold" fill="white">C</text></g>
                          <g><circle cx={20} cy={50 + h * cellSize / 2} r="20" fill="#10b981" /><text x={20} y={50 + h * cellSize / 2 + 7} textAnchor="middle" fontSize="18" fontWeight="bold" fill="white">D</text></g>
                        </svg>
                      );
                    })()}
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="space-y-6">
              <Card className="rounded-3xl shadow-lg">
                <CardHeader><CardTitle>{t('base_dimensions')}</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div><Label>{language === 'tr' ? 'En (m)' : 'Width (m)'}</Label><Input type="number" value={baseWidth} onChange={(e) => setBaseWidth(parseInt(e.target.value) || 10)} className="rounded-xl" /></div>
                  <div><Label>{language === 'tr' ? 'Boy (m)' : 'Height (m)'}</Label><Input type="number" value={baseHeight} onChange={(e) => setBaseHeight(parseInt(e.target.value) || 8)} className="rounded-xl" /></div>
                  <div className="bg-purple-50 p-4 rounded-xl">
                    <p className="font-semibold">{language === 'tr' ? 'Toplam Alan:' : 'Total Area:'} {totalArea} m²</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="rounded-3xl shadow-lg">
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <CardTitle>{language === 'tr' ? 'Kenar Durumu' : 'Side Status'}</CardTitle>
                    <Button onClick={handleToggleAdvanced} size="sm" variant="outline" className="rounded-full">
                      {showSegmentControls ? (language === 'tr' ? 'Basit Mod' : 'Simple') : (language === 'tr' ? 'Geli\u015fmi\u015f' : 'Advanced')}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {!showSegmentControls ? (
                    <div className="space-y-2">
                      {[
                        {s:'north',l:'A',color:'#ef4444'},
                        {s:'east', l:'B',color:'#f59e0b'},
                        {s:'south',l:'C',color:'#8b5cf6'},
                        {s:'west', l:'D',color:'#10b981'}
                      ].map(({s,l,color}) => (
                        <div key={s} className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{backgroundColor: color}}>
                            {l}
                          </div>
                          <div className="flex flex-1 rounded-lg overflow-hidden border border-gray-200 shadow-sm">
                            <button
                              onClick={() => { if (!openSides.includes(s)) toggleSide(s); }}
                              className={`flex-1 py-2 text-xs font-semibold transition-all ${openSides.includes(s) ? 'bg-green-500 text-white shadow-inner' : 'bg-white text-gray-400 hover:bg-green-50 hover:text-green-600'}`}
                            >
                              {language === 'tr' ? 'Açık' : 'Open'}
                            </button>
                            <div className="w-px bg-gray-200" />
                            <button
                              onClick={() => { if (openSides.includes(s)) toggleSide(s); }}
                              className={`flex-1 py-2 text-xs font-semibold transition-all ${!openSides.includes(s) ? 'bg-red-500 text-white shadow-inner' : 'bg-white text-gray-400 hover:bg-red-50 hover:text-red-600'}`}
                            >
                              {language === 'tr' ? 'Kapalı' : 'Closed'}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div>
                      {/* 4 Tab: A / B / C / D */}
                      <div className="flex rounded-xl overflow-hidden border border-gray-200 mb-3">
                        {[
                          {tab:'A', color:'#ef4444', label: language==='tr'?'Kuzey':'North'},
                          {tab:'B', color:'#f59e0b', label: language==='tr'?'Doğu':'East'},
                          {tab:'C', color:'#8b5cf6', label: language==='tr'?'Güney':'South'},
                          {tab:'D', color:'#10b981', label: language==='tr'?'Batı':'West'},
                        ].map(({tab, color}) => {
                          const groups = getAllSegmentGroups().filter(g => g.id.startsWith(tab));
                          const openCount = groups.filter(g => openSegments.includes(g.id)).length;
                          return (
                            <button
                              key={tab}
                              onClick={() => setActiveSegmentTab(tab)}
                              className={`flex-1 py-2 text-xs font-bold transition-all flex flex-col items-center gap-0.5 ${activeSegmentTab === tab ? 'text-white shadow-inner' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
                              style={activeSegmentTab === tab ? {backgroundColor: color} : {}}
                            >
                              <span>{tab}</span>
                              {groups.length > 0 && (
                                <span className="text-[9px] opacity-80">{openCount}/{groups.length}</span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                      {/* Active tab segments */}
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {(() => {
                          const tabGroups = getAllSegmentGroups().filter(g => g.id.startsWith(activeSegmentTab));
                          if (tabGroups.length === 0) return <p className="text-xs text-gray-400 text-center py-3">{language==='tr'?'Bu kenarda segment yok':'No segments on this side'}</p>;
                          return tabGroups.map(group => (
                            <div key={group.id} className="flex items-center gap-2">
                              <span className="text-xs font-bold w-8 text-center flex-shrink-0" style={{color: activeSegmentTab==='A'?'#ef4444':activeSegmentTab==='B'?'#f59e0b':activeSegmentTab==='C'?'#8b5cf6':'#10b981'}}>{group.id}</span>
                              <span className="text-xs text-gray-400 w-8 flex-shrink-0">{group.segments.length}m</span>
                              <div className="flex flex-1 rounded-lg overflow-hidden border border-gray-200 shadow-sm">
                                <button
                                  onClick={() => { if (!openSegments.includes(group.id)) toggleSegment(group.id); }}
                                  className={`flex-1 py-1.5 text-xs font-semibold transition-all ${openSegments.includes(group.id) ? 'bg-green-500 text-white' : 'bg-white text-gray-400 hover:bg-green-50 hover:text-green-600'}`}
                                >
                                  {language === 'tr' ? 'Açık' : 'Open'}
                                </button>
                                <div className="w-px bg-gray-200" />
                                <button
                                  onClick={() => { if (openSegments.includes(group.id)) toggleSegment(group.id); }}
                                  className={`flex-1 py-1.5 text-xs font-semibold transition-all ${!openSegments.includes(group.id) ? 'bg-red-500 text-white' : 'bg-white text-gray-400 hover:bg-red-50 hover:text-red-600'}`}
                                >
                                  {language === 'tr' ? 'Kapalı' : 'Closed'}
                                </button>
                              </div>
                            </div>
                          ));
                        })()}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              <div className="space-y-2 text-xs">
                <div className="flex items-center gap-2"><div className="w-4 h-4 bg-blue-600"></div><span>{language === 'tr' ? 'Ana Alan' : 'Base'}</span></div>
                <div className="flex items-center gap-2"><div className="w-4 h-4 bg-blue-300"></div><span>{language === 'tr' ? '\u00c7\u0131k\u0131nt\u0131' : 'Extrusion'}</span></div>
              </div>

              <div className="flex gap-3">
                <Button onClick={() => setShowPreview(true)} variant="outline" className="flex-1 border-2 border-sky-600 text-sky-600 hover:bg-sky-50 rounded-full py-6">
                  <Eye className="h-4 w-4 mr-2" />{language === 'tr' ? '\u00d6n \u0130zleme' : 'Preview'}
                </Button>
                <Button onClick={saveLayout} className="flex-1 bg-purple-700 rounded-full py-6 text-lg">
                  <Save className="h-4 w-4 mr-2" />{t('save')}
                </Button>
              </div>
              
              <Button onClick={() => setShowPreview(true)} variant="outline" className="w-full border-2 border-sky-600 text-sky-600 hover:bg-sky-50 rounded-full py-6 text-lg">
                <Eye className="h-4 w-4 mr-2" />{language === 'tr' ? 'On Izleme' : 'Preview'}
              </Button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setShowPreview(false)}>
          <Card className="border-0 shadow-2xl rounded-3xl max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="text-2xl">{language === 'tr' ? 'Layout On Izleme' : 'Layout Preview'}</CardTitle>
                <Button onClick={() => setShowPreview(false)} variant="ghost" className="text-2xl hover:bg-gray-100 rounded-full">✕</Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="bg-gray-50 rounded-2xl p-8 flex items-center justify-center">
                {!baseWidth || !baseHeight ? (
                  <p className="text-gray-500">{language === 'tr' ? 'Boyut giriniz' : 'Enter dimensions'}</p>
                ) : (() => {
                  const w = parseInt(baseWidth);
                  const h = parseInt(baseHeight);
                  const cellSize = 25;
                  
                  return (
                    <svg width={w * cellSize + 100} height={h * cellSize + 100}>
                      {/* Grid cells */}
                      {selectedCells.map(([x, y]) => {
                        const inside = x < w && y < h;
                        return (
                          <rect key={`prev-${x}-${y}`} x={50 + x * cellSize} y={50 + y * cellSize} width={cellSize} height={cellSize} fill={inside ? '#3b82f6' : '#93c5fd'} />
                        );
                      })}
                      
                      {/* Sınır */}
                      <rect x={50} y={50} width={w * cellSize} height={h * cellSize} fill="none" stroke="#0ea5e9" strokeWidth="3" strokeDasharray="8,4" />
                      
                      {/* Kenarlar - Her segment kendi rengi */}
                      {selectedCells.length > 0 && (
                        <>
                          {getEdgeSegments('north').map((seg, i) => (
                            <line key={`pn-${i}`} x1={50 + seg.x1 * cellSize} y1={50 + seg.y1 * cellSize} x2={50 + seg.x2 * cellSize} y2={50 + seg.y2 * cellSize} stroke={getSegmentColor('north', seg.x1, seg.y1)} strokeWidth="10" strokeLinecap="square" />
                          ))}
                          {getEdgeSegments('east').map((seg, i) => (
                            <line key={`pe-${i}`} x1={50 + seg.x1 * cellSize} y1={50 + seg.y1 * cellSize} x2={50 + seg.x2 * cellSize} y2={50 + seg.y2 * cellSize} stroke={getSegmentColor('east', seg.x1, seg.y1)} strokeWidth="10" strokeLinecap="square" />
                          ))}
                          {getEdgeSegments('south').map((seg, i) => (
                            <line key={`ps-${i}`} x1={50 + seg.x1 * cellSize} y1={50 + seg.y1 * cellSize} x2={50 + seg.x2 * cellSize} y2={50 + seg.y2 * cellSize} stroke={getSegmentColor('south', seg.x1, seg.y1)} strokeWidth="10" strokeLinecap="square" />
                          ))}
                          {getEdgeSegments('west').map((seg, i) => (
                            <line key={`pw-${i}`} x1={50 + seg.x1 * cellSize} y1={50 + seg.y1 * cellSize} x2={50 + seg.x2 * cellSize} y2={50 + seg.y2 * cellSize} stroke={getSegmentColor('west', seg.x1, seg.y1)} strokeWidth="10" strokeLinecap="square" />
                          ))}
                        </>
                      )}
                      
                      {/* A,B,C,D */}
                      <g><circle cx={50 + w * cellSize / 2} cy={20} r="22" fill="#ef4444" /><text x={50 + w * cellSize / 2} y={28} textAnchor="middle" fontSize="20" fontWeight="bold" fill="white">A</text></g>
                      <g><circle cx={50 + w * cellSize + 35} cy={50 + h * cellSize / 2} r="22" fill="#f59e0b" /><text x={50 + w * cellSize + 35} y={50 + h * cellSize / 2 + 8} textAnchor="middle" fontSize="20" fontWeight="bold" fill="white">B</text></g>
                      <g><circle cx={50 + w * cellSize / 2} cy={50 + h * cellSize + 35} r="22" fill="#8b5cf6" /><text x={50 + w * cellSize / 2} y={50 + h * cellSize + 43} textAnchor="middle" fontSize="20" fontWeight="bold" fill="white">C</text></g>
                      <g><circle cx={15} cy={50 + h * cellSize / 2} r="22" fill="#10b981" /><text x={15} y={50 + h * cellSize / 2 + 8} textAnchor="middle" fontSize="20" fontWeight="bold" fill="white">D</text></g>
                      
                      {/* Boyut */}
                      <text x={50 + w * cellSize / 2} y={50 + h * cellSize / 2} textAnchor="middle" fontSize="20" fontWeight="bold" fill="#0c4a6e">
                        {w}m x {h}m
                      </text>
                      <text x={50 + w * cellSize / 2} y={50 + h * cellSize / 2 + 25} textAnchor="middle" fontSize="16" fill="#075985">
                        {totalArea} m²
                      </text>
                    </svg>
                  );
                })()}
              </div>
              
              <div className="mt-6 grid grid-cols-2 gap-4 text-sm">
                <div className="flex items-center gap-2"><div className="w-6 h-1 bg-green-500 rounded"></div><span>{language === 'tr' ? 'Acik Kenar' : 'Open Side'}</span></div>
                <div className="flex items-center gap-2"><div className="w-6 h-1 bg-red-500 rounded"></div><span>{language === 'tr' ? 'Kapali Kenar' : 'Closed Side'}</span></div>
                <div className="flex items-center gap-2"><div className="w-6 h-6 bg-blue-600"></div><span>{language === 'tr' ? 'Ana Alan' : 'Base Area'}</span></div>
                <div className="flex items-center gap-2"><div className="w-6 h-6 bg-blue-300"></div><span>{language === 'tr' ? 'Cikinti' : 'Extrusion'}</span></div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default BlueZoneLayoutAdd;
